'use client'

import { useEffect, useState } from 'react'
import i18next, { type Namespace } from 'i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import resourcesToBackend from 'i18next-resources-to-backend'
import {
  initReactI18next,
  useTranslation as useTranslationOrg,
} from 'react-i18next'

import { Locale, useLocale } from '@rewardops/ui'

import { getServerTenant } from '~/utils/tenant-utils'
import { useApi } from '~/components/hooks/use-api'
import { getOptions, languages } from './settings'

const runsOnServerSide = typeof window === 'undefined'

i18next
  .use(initReactI18next)
  .use(LanguageDetector)
  .use(
    resourcesToBackend(
      (language: string, namespace: string) =>
        import(`./${language}/${namespace}-translation.json`),
    ),
  )
  .init({
    ...getOptions(),
    lng: undefined, // let detect the language on client side
    detection: {
      order: ['path', 'htmlTag', 'cookie', 'navigator'],
    },
    preload: runsOnServerSide ? languages : [],
  })

// TODO: Use Nextjs Head component and add title in there
export function useTranslation(
  ns: Namespace = 'common',
  options?: { [key: string]: any },
  ssrOverride?: Partial<Locale>,
) {
  const { language } = useLocale(ssrOverride)
  const { tenant } = useApi()
  const programName = tenant?.programConfig?.programName
  const ret = useTranslationOrg(programName ?? ns, options)
  const { i18n } = ret

  if (runsOnServerSide && language && i18n.language !== language) {
    i18n.changeLanguage(language)
  } else {
    const [activeLng, setActiveLng] = useState<string | undefined>(
      i18n.language,
    )

    useEffect(() => {
      if (activeLng === i18n.language) return
      setActiveLng(i18n.language)
    }, [activeLng, i18n.language])

    useEffect(() => {
      if (!language || i18n.language === language) return
      i18n.changeLanguage(language)
      if ((tenant as any)?.public?.siteTitle) {
        const newTitle =
          (tenant as any).public.siteTitle[language] ??
          (tenant as any).public.siteTitle['en'] ??
          'Storefront'
        document.title = newTitle
      }
    }, [language, i18n, tenant])

    // Add language overrides
    if (tenant.locale.overrides && (language === 'en' || language === 'fr')) {
      i18n.addResourceBundle(
        language,
        ns.toString(),
        tenant.locale.overrides[language],
        true,
        true,
      )
    }
  }

  return ret
}
